import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { allOpps } from '../lib/salesData';
import { usd, type OppStage } from '../lib/clientDeepData';

const STAGES: [OppStage, string][] = [
  ['inquiry', 'Inquiry'], ['qualified', 'Qualified'], ['quoted', 'Quoted'], ['negotiating', 'Negotiating'], ['won', 'Won'], ['lost', 'Lost'],
];
const WEIGHT: Record<OppStage, number> = { inquiry: 0.1, qualified: 0.25, quoted: 0.5, negotiating: 0.75, won: 1, lost: 0 };

export default function Pipeline() {
  const { demo } = useAuth();
  const { data, loading } = useAccounts(demo);
  const nav = useNavigate();
  const opps = useMemo(() => allOpps(data?.accounts ?? []), [data]);

  if (loading) return <div className="loading"><div className="spinner" /> Loading pipeline…</div>;

  const open = opps.filter((o) => o.stage !== 'won' && o.stage !== 'lost');
  const totalValue = open.reduce((s, o) => s + o.value, 0);
  const weighted = open.reduce((s, o) => s + o.value * WEIGHT[o.stage], 0);
  const won = opps.filter((o) => o.stage === 'won').reduce((s, o) => s + o.value, 0);

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Opportunity Pipeline</div><div className="page-sub">Track open sales opportunities before they become quotes or orders.</div></div>
        <button className="btn primary" onClick={() => nav('/quote-engine')}>＋ New quote</button>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Open Opportunities</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{open.length}</div><div className="metric-note">in active stages</div></div>
        <div className="metric-card"><div className="metric-label">Pipeline Value</div><div className="metric-value">{usd(totalValue)}</div><div className="metric-note">unweighted</div></div>
        <div className="metric-card"><div className="metric-label">Weighted</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{usd(Math.round(weighted))}</div><div className="metric-note">by stage probability</div></div>
        <div className="metric-card"><div className="metric-label">Won</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{usd(won)}</div><div className="metric-note">closed business</div></div>
      </div>

      <div className="kanban pipeline-board">
        {STAGES.map(([key, label]) => {
          const items = opps.filter((o) => o.stage === key);
          const val = items.reduce((s, o) => s + o.value, 0);
          return (
            <div className="kanban-col" key={key}>
              <div className="kanban-title"><span>{label}</span><span>{items.length}</span></div>
              <div className="col-sum">{usd(val)}</div>
              {items.map((o) => (
                <div className="task-card" key={o.id + o.clientId} onClick={() => nav(`/client?id=${encodeURIComponent(o.clientId)}`)} style={{ cursor: 'pointer' }}>
                  <h4>{o.title}</h4>
                  <div className="task-meta"><span className="tag dp-sales">{o.clientName}</span></div>
                  <div className="sub">{usd(o.value)} · {o.owner} · {o.age}</div>
                </div>
              ))}
              {items.length === 0 && <div className="sub" style={{ padding: 6 }}>—</div>}
            </div>
          );
        })}
      </div>
    </>
  );
}
